import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline } from 'react-leaflet';
import L from 'leaflet';
import { collection, onSnapshot, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { db } from '../firebase';
import AdminSidebar from '../components/AdminSidebar';
import 'leaflet/dist/leaflet.css';
import '../pages/DriverTracking.css';
import carGreen from '../assets/1.png';
import carBlue from '../assets/2.png';
import carRed from '../assets/3.png';
import { Link } from 'react-router-dom';

// Car icon definitions
const carIcons = {
  green: new L.Icon({ iconUrl: carGreen, iconSize: [50, 50], iconAnchor: [25, 50], popupAnchor: [0, -40] }),
  blue: new L.Icon({ iconUrl: carBlue, iconSize: [50, 50], iconAnchor: [25, 50], popupAnchor: [0, -40] }),
  red: new L.Icon({ iconUrl: carRed, iconSize: [50, 50], iconAnchor: [25, 50], popupAnchor: [0, -40] }),
};

// تحديد لون السيارة على الخريطة
const getMarkerColor = (driver) => {
  // تحقق من حالة الاتصال
  if (!driver.isOnline) return 'red';
  
  // ثم تحقق من الحالات الأخرى
  if (!driver.isMoving) return 'red';
  if (driver.orderStatus === 'في الطريق') return 'blue';
  return 'green';
};

// مكون لعرض حالة الاتصال للسائق
const OnlineStatusPill = ({ driver }) => {
  return (
    <span className={`status-pill ${driver.isOnline ? 'status-online' : 'status-offline'}`}>
      {driver.isOnline ? 'متصل' : 'غير متصل'}
    </span>
  );
};

// مكون لعرض حالة الإتاحة للسائق
const AvailabilityPill = ({ driver }) => {
  // إذا كان السائق غير متصل، فهو غير متاح بالتأكيد
  const isAvailable = driver.isOnline && driver.isAvailable;
  
  return (
    <span className={`status-pill ${isAvailable ? 'status-available' : 'status-busy'}`}>
      {isAvailable ? 'متاح' : 'مشغول'}
    </span>
  );
};

// مكون لتحديث مركز الخريطة
function MapUpdater({ drivers }) {
  const map = useMap();
  
  useEffect(() => {
    if (drivers && drivers.length > 0) {
      // حساب مركز متوسط لمواقع السائقين
      const avgLat = drivers.reduce((sum, d) => sum + d.location.lat, 0) / drivers.length;
      const avgLng = drivers.reduce((sum, d) => sum + d.location.lng, 0) / drivers.length;
      
      // تحديث مركز الخريطة فقط إذا تغير بشكل كبير
      const currentCenter = map.getCenter();
      if (Math.abs(currentCenter.lat - avgLat) > 0.02 || 
          Math.abs(currentCenter.lng - avgLng) > 0.02) {
        map.setView([avgLat, avgLng], map.getZoom(), { animate: true });
      }
    }
  }, [drivers, map]);
  
  return null;
}

// مكون لعرض المؤشر مع تأثير الحركة
function MovingMarker({ position, icon, popup, id, isMoving }) {
  const markerRef = useRef(null);
  const [prevPosition, setPrevPosition] = useState(position);
  
  // تحديث الموقع مع تأثير الحركة
  useEffect(() => {
    if (markerRef.current && prevPosition) {
      // فقط تحريك المؤشر إذا تغير الموقع بشكل كافٍ (لتجنب الاهتزازات)
      const distance = L.latLng(position).distanceTo(L.latLng(prevPosition));
      
      if (distance > 5) {  // أكثر من 5 أمتار
        if (isMoving) {
          markerRef.current.setLatLng(position);
        } else {
          // انتقال مباشر بدون حركة سلسة للسائق المتوقف
          markerRef.current.setLatLng(position);
        }
        setPrevPosition(position);
      }
    }
  }, [position, prevPosition, isMoving]);
  
  return (
    <Marker
      position={position}
      icon={icon}
      ref={markerRef}
      key={`marker-${id}`}
    >
      {popup}
    </Marker>
  );
}

const DriverTracking = () => {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [messageBoxes, setMessageBoxes] = useState({});
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('map');
  const [loading, setLoading] = useState(true);
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  const [filterActiveOnly, setFilterActiveOnly] = useState(false);
  
  // State for tracking history
  const [historyDriver, setHistoryDriver] = useState('');
  const [historyDate, setHistoryDate] = useState(new Date().toISOString().split('T')[0]);
  const [trackingHistory, setTrackingHistory] = useState({ movementData: [], pathPolyline: [] });
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyStats, setHistoryStats] = useState({
    totalDistance: 0,
    averageSpeed: 0,
    maxSpeed: 0,
    stopsCount: 0,
  });
  const [mapCenter, setMapCenter] = useState([29.3759, 47.9774]); // Default: Kuwait
  const [mapZoom, setMapZoom] = useState(12);

  // استخدام مرجع للتحقق من التحديثات
  const updateTimerRef = useRef(null);
  // مرجع unsubscribe
  const unsubscribeRef = useRef(null);
  
  // تحميل بيانات السائقين
  useEffect(() => {
    setLoading(true);
    
    const setupDriversListener = () => {
      try {
        // إنشاء استعلام مع ترتيب حسب آخر تحديث
        const driversQuery = query(
          collection(db, 'drivers'), 
          orderBy('lastUpdate', 'desc'),
          limit(50)
        );
        
        // مستمع البيانات مع التعامل مع الأخطاء
        const unsubscribe = onSnapshot(driversQuery, (snapshot) => {
          try {
            // فحص ما إذا كانت البيانات من ذاكرة التخزين المؤقت
            const isFromCache = snapshot.metadata.fromCache;
            if (isFromCache) {
              console.log("البيانات من ذاكرة التخزين المؤقت - قد لا تكون محدثة");
            }
            
            const updatedDrivers = snapshot.docs
              .map(doc => {
                const data = doc.data();
                const location = data.location || {};
                const lastUpdate = data.lastUpdate?.toDate() || new Date();
                const timeSinceLastUpdate = Date.now() - lastUpdate.getTime();
                
                // تخطي السائقين بدون إحداثيات
                if (!location || !location.lat || !location.lng) {
                  console.warn(`سائق بدون إحداثيات: ${doc.id}`);
                  return null;
                }
                
                // التحقق من ما إذا كان السائق نشط مؤخرًا (خلال الـ 15 دقيقة الماضية)
                const isRecentlyActive = timeSinceLastUpdate < 15 * 60 * 1000;
                
                // الحقول الأساسية للسائق
                return {
                  id: doc.id,
                  name: data.name || 'غير معروف',
                  phone: data.phone || 'غير متوفر',
                  location: { 
                    lat: parseFloat(location.lat), 
                    lng: parseFloat(location.lng) 
                  },
                  isMoving: !!data.isMoving,
                  isAvailable: !!data.isAvailable,
                  isRecentlyActive,
                  // استخدام الحقل "status" من قاعدة البيانات لتحديد حالة الاتصال
                  isOnline: data.status === 'online',
                  orderStatus: data.orderStatus || 'لا يوجد طلب',
                  speed: parseFloat(data.speed || 0),
                  lastUpdate,
                  timeSinceLastUpdate,
                  fcmToken: data.fcmToken || '',
                  lastModified: data.lastModified ? data.lastModified.toDate() : new Date()
                };
              })
              .filter(Boolean); // إزالة القيم الفارغة
              
            // تصفية السائقين النشطين فقط إذا كان الفلتر مفعل
            const filteredDrivers = filterActiveOnly 
              ? updatedDrivers.filter(driver => driver.isRecentlyActive)
              : updatedDrivers;
            
            console.log('تم تحميل السائقين:', filteredDrivers.length);
            setDrivers(filteredDrivers);
            setLastUpdateTime(new Date());
            setLoading(false);
          } catch (error) {
            console.error('خطأ في معالجة بيانات السائقين:', error);
            setLoading(false);
          }
        }, (error) => {
          console.error('خطأ في الاستماع للتغييرات:', error);
          setLoading(false);
          
          // في حالة الخطأ، نحاول إعادة الاتصال بعد 5 ثوانٍ
          setTimeout(() => {
            if (unsubscribeRef.current) {
              unsubscribeRef.current();
            }
            setupDriversListener();
          }, 5000);
        });
        
        // حفظ مرجع unsubscribe للتنظيف لاحقًا
        unsubscribeRef.current = unsubscribe;
      } catch (error) {
        console.error('خطأ في إعداد الاستماع للتغييرات:', error);
        setLoading(false);
        
        // في حالة الخطأ، نحاول إعادة الاتصال بعد 5 ثوانٍ
        setTimeout(setupDriversListener, 5000);
      }
    };
    
    // بدء الاستماع
    setupDriversListener();
    
    // تنظيف المستمع عند إزالة المكون
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      
      if (updateTimerRef.current) {
        clearInterval(updateTimerRef.current);
      }
    };
  }, [filterActiveOnly]);

  // تحديث timeSinceLastUpdate كل 5 ثواني
  useEffect(() => {
    updateTimerRef.current = setInterval(() => {
      setDrivers(prevDrivers => 
        prevDrivers.map(driver => {
          const timeSinceLastUpdate = Date.now() - driver.lastUpdate.getTime();
          const isRecentlyActive = timeSinceLastUpdate < 15 * 60 * 1000;
          
          // عدم تغيير حالة التصال (isOnline)، لأنها تأتي من الحقل 'status' في Firebase
          // عندما يسجل السائق الخروج، سيتغير حقل 'status' إلى 'offline' في قاعدة البيانات
          return {
            ...driver,
            timeSinceLastUpdate,
            isRecentlyActive
          };
        })
      );
    }, 5000); // تحديث كل 5 ثواني
  
    return () => {
      if (updateTimerRef.current) {
        clearInterval(updateTimerRef.current);
      }
    };
  }, []);
  
  // إعادة تحميل البيانات يدويًا
  const refreshData = () => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }
    
    setLoading(true);
    
    try {
      // إنشاء استعلام مع ترتيب حسب آخر تحديث
      const driversQuery = query(
        collection(db, 'drivers'), 
        orderBy('lastUpdate', 'desc'),
        limit(50)
      );
      
      const unsubscribe = onSnapshot(driversQuery, (snapshot) => {
        try {
          const updatedDrivers = snapshot.docs
            .map(doc => {
              const data = doc.data();
              const location = data.location || {};
              const lastUpdate = data.lastUpdate?.toDate() || new Date();
              const timeSinceLastUpdate = Date.now() - lastUpdate.getTime();
              
              // تخطي السائقين بدون إحداثيات
              if (!location || !location.lat || !location.lng) {
                return null;
              }
              
              // التحقق من ما إذا كان السائق نشط مؤخرًا (خلال الـ 15 دقيقة الماضية)
              const isRecentlyActive = timeSinceLastUpdate < 15 * 60 * 1000;
              
              return {
                id: doc.id,
                name: data.name || 'غير معروف',
                phone: data.phone || 'غير متوفر',
                location: { 
                  lat: parseFloat(location.lat), 
                  lng: parseFloat(location.lng) 
                },
                isMoving: !!data.isMoving,
                isAvailable: !!data.isAvailable,
                isRecentlyActive,
                // استخدام الحقل "status" من قاعدة البيانات لتحديد حالة الاتصال
                isOnline: data.status === 'online',
                orderStatus: data.orderStatus || 'لا يوجد طلب',
                speed: parseFloat(data.speed || 0),
                lastUpdate,
                timeSinceLastUpdate,
                fcmToken: data.fcmToken || '',
                lastModified: data.lastModified ? data.lastModified.toDate() : new Date()
              };
            })
            .filter(Boolean);
            
          // تصفية السائقين النشطين فقط إذا كان الفلتر مفعل
          const filteredDrivers = filterActiveOnly 
            ? updatedDrivers.filter(driver => driver.isRecentlyActive)
            : updatedDrivers;
          
          setDrivers(filteredDrivers);
          setLastUpdateTime(new Date());
        } catch (error) {
          console.error('خطأ في جلب بيانات السائقين:', error);
        } finally {
          setLoading(false);
        }
      });
      
      // حفظ مرجع unsubscribe للتنظيف لاحقًا
      unsubscribeRef.current = unsubscribe;
    } catch (error) {
      console.error('خطأ في إعداد الاستماع للتغييرات:', error);
      setLoading(false);
    }
  };

  // تغيير وضع الفلتر
  const toggleFilter = () => {
    setFilterActiveOnly(!filterActiveOnly);
  };

  // التعامل مع إرسال رسالة للسائق
  const handleSendMessage = async (driverId) => {
    const message = messageBoxes[driverId];
    if (!message?.trim()) {
      alert('الرجاء إدخال نص الرسالة');
      return;
    }

    const driver = drivers.find(d => d.id === driverId);
    if (!driver?.fcmToken) {
      alert("❌ هذا السائق لا يحتوي على FCM Token");
      return;
    }

    setSending(true);
    try {
      const res = await fetch("https://us-central1-atdelivery-9d139.cloudfunctions.net/sendOrderNotification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fcmToken: driver.fcmToken,
          title: "رسالة من الإدارة",
          body: message,
          type: "admin_message"
        }),
      });
      
      if (!res.ok) {
        throw new Error(`خطأ في الاستجابة: ${res.status}`);
      }

      const data = await res.json();
      if (data.success) {
        alert("✅ تم إرسال الرسالة بنجاح");
        setMessageBoxes(prev => ({ ...prev, [driverId]: '' }));
        setSelectedDriver(null);
      } else {
        alert(`❌ حدث خطأ أثناء الإرسال: ${data.error || 'سبب غير معروف'}`);
      }
    } catch (e) {
      console.error("خطأ في إرسال الرسالة:", e);
      alert(`❌ حدث خطأ تقني: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  // تغيير نص الرسالة
  const handleMessageChange = (driverId, text) => {
    setMessageBoxes(prev => ({ ...prev, [driverId]: text }));
  };

  // تنسيق الوقت منذ آخر تحديث
  const formatTimeSince = (timeSinceUpdate) => {
    const seconds = Math.floor(timeSinceUpdate / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `منذ ${hours} ${hours === 1 ? 'ساعة' : 'ساعات'}`;
    } else if (minutes > 0) {
      return `منذ ${minutes} ${minutes === 1 ? 'دقيقة' : 'دقائق'}`;
    } else {
      return `منذ ${seconds} ${seconds === 1 ? 'ثانية' : 'ثواني'}`;
    }
  };

  // الحصول على وصف حالة السائق
  const getDriverStatusText = (driver) => {
    if (!driver.isOnline) {
      return 'غير متصل';
    }
    
    return driver.isMoving ? 'يتحرك' : 'متوقف';
  };

  // الحصول على صنف حالة السائق
  const getDriverStatusClass = (driver) => {
    if (!driver.isOnline) {
      return 'offline';
    }
    
    return driver.isMoving ? 'moving' : 'stopped';
  };

  // Calculation functions for history
  const formatTime = useCallback((date) => {
    return date.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }, []);

  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c; // Distance in kilometers
    return distance;
  }, []);

  const isStopPoint = useCallback((movement) => {
    return movement.isMoving === false || movement.speed < 3; // Consider less than 3 km/h as a stop
  }, []);

  // Fetch driver movement history
  const fetchDriverMovements = useCallback(async () => {
    if (!historyDriver || !historyDate) {
      setTrackingHistory({ movementData: [], pathPolyline: [] });
      return;
    }

    setLoadingHistory(true);
    try {
      // Define date range (start and end of selected day)
      const selectedDateObj = new Date(historyDate);
      const startOfDay = new Date(selectedDateObj);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(selectedDateObj);
      endOfDay.setHours(23, 59, 59, 999);

      // Firestore query
      const movementsRef = collection(db, 'driverMovements');
      const movementsQuery = query(
        movementsRef,
        where('driverId', '==', historyDriver),
        where('timestamp', '>=', startOfDay),
        where('timestamp', '<=', endOfDay),
        orderBy('timestamp', 'asc')
      );

      const movementsSnapshot = await getDocs(movementsQuery);
      
      // Transform data
      const movements = movementsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          location: data.location || { lat: 0, lng: 0 },
          timestamp: data.timestamp ? data.timestamp.toDate() : new Date(),
          formattedTime: data.timestamp ? formatTime(data.timestamp.toDate()) : "",
          speed: data.speed || 0,
          isMoving: data.isMoving !== undefined ? data.isMoving : (data.speed > 3),
          driverId: data.driverId,
          driverName: data.driverName || ""
        };
      });

      // Create trip path (polyline) and stop points
      if (movements.length > 0) {
        // Prepare path points for polyline
        const polylinePoints = movements.map(m => [m.location.lat, m.location.lng]);
        
        // Set map center to first point if available
        if (polylinePoints.length > 0) {
          setMapCenter(polylinePoints[0]);
          setMapZoom(14); // Closer zoom for better visibility
        }

        // Calculate statistics
        let totalDistance = 0;
        let speedSum = 0;
        let maxSpeed = 0;
        let stopsCounter = 0;
        let isCurrentlyStopped = false;

        for (let i = 1; i < movements.length; i++) {
          const prevMovement = movements[i-1];
          const currentMovement = movements[i];
          
          // Calculate distance between points
          const distance = calculateDistance(
            prevMovement.location.lat, prevMovement.location.lng,
            currentMovement.location.lat, currentMovement.location.lng
          );
          
          totalDistance += distance;
          
          // Update speed statistics
          speedSum += currentMovement.speed;
          maxSpeed = Math.max(maxSpeed, currentMovement.speed);
          
          // Count stops
          if (isStopPoint(currentMovement)) {
            if (!isCurrentlyStopped) {
              stopsCounter++; // Start of a new stop
              isCurrentlyStopped = true;
            }
          } else {
            isCurrentlyStopped = false;
          }
        }

        // Update statistics
        setHistoryStats({
          totalDistance: parseFloat(totalDistance.toFixed(2)),
          averageSpeed: movements.length > 0 ? parseFloat((speedSum / movements.length).toFixed(1)) : 0,
          maxSpeed: parseFloat(maxSpeed.toFixed(1)),
          stopsCount: stopsCounter
        });

        setTrackingHistory({
          movementData: movements,
          pathPolyline: polylinePoints
        });
      } else {
        // If no data, reset everything
        setTrackingHistory({ movementData: [], pathPolyline: [] });
        setMapCenter([29.3759, 47.9774]);
        setMapZoom(12);
        setHistoryStats({
          totalDistance: 0,
          averageSpeed: 0,
          maxSpeed: 0,
          stopsCount: 0
        });
      }
    } catch (error) {
      console.error('Error fetching driver movements:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [historyDriver, historyDate, formatTime, calculateDistance, isStopPoint]);

  // Fetch driver history when driver or date changes
  useEffect(() => {
    if (activeTab === 'history' && historyDriver && historyDate) {
      fetchDriverMovements();
    }
  }, [historyDriver, historyDate, activeTab, fetchDriverMovements]);

  return (
    <div className="driver-tracking-layout">
      <AdminSidebar />
      <div className="driver-tracking-content">
        <div className="driver-tracking-header">
          <h1>تتبع السائقين</h1>
          <div className="tracking-controls">
            <div className="filter-toggle">
              <label className="toggle-switch">
                <input 
                  type="checkbox" 
                  checked={filterActiveOnly}
                  onChange={toggleFilter}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="toggle-label">عرض السائقين النشطين فقط</span>
            </div>
            <button 
              className="refresh-button"
              onClick={refreshData}
            >
              تحديث البيانات
            </button>
            <Link to="/admin/driver-tracking-history" className="tracking-history-button">
              سجل تحركات السائقين
            </Link>
            <div className="update-info">
              آخر تحديث: {formatTimeSince(Date.now() - lastUpdateTime.getTime())}
            </div>
          </div>
          <div className="tab-selector">
            <button 
              className={`tab-button ${activeTab === 'map' ? 'active' : ''}`} 
              onClick={() => setActiveTab('map')}
            >
              الخريطة
            </button>
            <button 
              className={`tab-button ${activeTab === 'list' ? 'active' : ''}`} 
              onClick={() => setActiveTab('list')}
            >
              قائمة السائقين
            </button>
            <button 
              className={`tab-button ${activeTab === 'history' ? 'active' : ''}`} 
              onClick={() => setActiveTab('history')}
            >
              سجل التحركات
            </button>
          </div>
        </div>

        <div className="dashboard-container">
          {loading && activeTab !== 'history' ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>جاري تحميل بيانات السائقين...</p>
            </div>
          ) : drivers.length === 0 && activeTab !== 'history' ? (
            <div className="no-data">
              <div className="no-data-icon">🚫</div>
              <p>لا يوجد سائقين متاحين حالياً.</p>
            </div>
          ) : activeTab === 'map' ? (
            <div className="map-container">
              <div className="map-legend">
                <div className="legend-item">
                  <div className="legend-color green"></div>
                  <span>متاح</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color blue"></div>
                  <span>في الطريق</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color red"></div>
                  <span>متوقف / غير نشط</span>
                </div>
              </div>
              <MapContainer center={[29.3759, 47.9774]} zoom={12} className="leaflet-map">
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <MapUpdater drivers={drivers} />
                {drivers.map(driver => (
                  <MovingMarker
                    key={driver.id}
                    id={driver.id}
                    position={driver.location}
                    icon={carIcons[getMarkerColor(driver)]}
                    isMoving={driver.isMoving && driver.isRecentlyActive}
                    popup={
                      <Popup>
                        <div className="driver-popup">
                          <h3>{driver.name}</h3>
                          <div className="popup-data">
                            <div className="popup-row">
                              <span className="popup-label">رقم الهاتف:</span>
                              <span className="popup-value">{driver.phone}</span>
                            </div>
                            <div className="popup-row">
                              <span className="popup-label">حالة الطلب:</span>
                              <span className="popup-value">{driver.orderStatus}</span>
                            </div>
                            <div className="popup-row">
                              <span className="popup-label">السرعة:</span>
                              <span className="popup-value">{driver.speed.toFixed(1)} كم/س</span>
                            </div>
                            <div className="popup-row">
                              <span className="popup-label">حالة الاتصال:</span>
                              <span className="popup-value">
                                <OnlineStatusPill driver={driver} />
                              </span>
                            </div>
                            <div className="popup-row">
                              <span className="popup-label">الحالة:</span>
                              <span className={`popup-value status-${getDriverStatusClass(driver)}`}>
                                {getDriverStatusText(driver)}
                              </span>
                            </div>
                            <div className="popup-row">
                              <span className="popup-label">آخر تحديث:</span>
                              <span className="popup-value">
                                {formatTimeSince(driver.timeSinceLastUpdate)}
                              </span>
                            </div>
                          </div>
                          <button 
                            className="popup-button"
                            onClick={() => {
                              setSelectedDriver(driver);
                              setActiveTab('list');
                            }}
                          >
                            إرسال رسالة
                          </button>
                        </div>
                      </Popup>
                    }
                  />
                ))}
              </MapContainer>
            </div>
          ) : activeTab === 'history' ? (
            <div className="tracking-history-section">
              <div className="tracking-history-controls">
                <div className="control-group">
                  <label>اختر السائق:</label>
                  <select 
                    value={historyDriver}
                    onChange={(e) => setHistoryDriver(e.target.value)}
                    className="driver-select"
                  >
                    <option value="">-- اختر السائق --</option>
                    {drivers.map((driver) => (
                      <option key={driver.id} value={driver.id}>
                        {driver.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="control-group">
                  <label>التاريخ:</label>
                  <input 
                    type="date" 
                    value={historyDate}
                    onChange={(e) => setHistoryDate(e.target.value)}
                    className="date-select"
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
              
              {loadingHistory ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>جاري تحميل بيانات الحركة...</p>
                </div>
              ) : !historyDriver ? (
                <div className="no-selection-message">
                  <p>الرجاء اختيار سائق لعرض سجل تحركاته</p>
                </div>
              ) : trackingHistory.movementData.length === 0 ? (
                <div className="no-data-message">
                  <p>لا توجد بيانات تحركات متاحة لهذا السائق في التاريخ المحدد</p>
                </div>
              ) : (
                <>
                  {/* Statistics Cards */}
                  <div className="stats-cards">
                    <div className="stat-card">
                      <div className="stat-value">{historyStats.totalDistance} كم</div>
                      <div className="stat-label">المسافة الإجمالية</div>
                    </div>
                    
                    <div className="stat-card">
                      <div className="stat-value">{historyStats.averageSpeed} كم/س</div>
                      <div className="stat-label">متوسط السرعة</div>
                    </div>
                    
                    <div className="stat-card">
                      <div className="stat-value">{historyStats.maxSpeed} كم/س</div>
                      <div className="stat-label">أقصى سرعة</div>
                    </div>
                    
                    <div className="stat-card">
                      <div className="stat-value">{historyStats.stopsCount}</div>
                      <div className="stat-label">عدد التوقفات</div>
                    </div>
                  </div>
                  
                  {/* Map Section */}
                  <div className="map-container">
                    <div className="map-legend">
                      <div className="legend-item">
                        <div className="legend-color moving"></div>
                        <span>نقاط الحركة</span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-color stopped"></div>
                        <span>نقاط التوقف</span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-color route"></div>
                        <span>المسار</span>
                      </div>
                    </div>
                    
                    <MapContainer center={mapCenter} zoom={mapZoom} className="leaflet-map">
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                      
                      {/* Path Line */}
                      {trackingHistory.pathPolyline.length > 0 && (
                        <Polyline
                          positions={trackingHistory.pathPolyline}
                          color="#2979ff"
                          weight={4}
                          opacity={0.7}
                        />
                      )}
                      
                      {/* Markers */}
                      {trackingHistory.movementData.map((movement) => (
                        <Marker
                          key={movement.id}
                          position={[movement.location.lat, movement.location.lng]}
                          icon={isStopPoint(movement) ? carIcons.red : carIcons.green}
                        >
                          <Popup>
                            <div className="movement-popup">
                              <h3>{movement.driverName || "سائق"}</h3>
                              <div className="popup-row">
                                <span className="popup-label">الوقت:</span>
                                <span className="popup-value">{movement.formattedTime}</span>
                              </div>
                              <div className="popup-row">
                                <span className="popup-label">السرعة:</span>
                                <span className="popup-value">{movement.speed.toFixed(1)} كم/س</span>
                              </div>
                              <div className="popup-row">
                                <span className="popup-label">الحالة:</span>
                                <span className="popup-value">
                                  {isStopPoint(movement) ? 'متوقف' : 'متحرك'}
                                </span>
                              </div>
                              <div className="popup-row">
                                <span className="popup-label">الموقع:</span>
                                <span className="popup-value">
                                  {movement.location.lat.toFixed(6)}, {movement.location.lng.toFixed(6)}
                                </span>
                              </div>
                            </div>
                          </Popup>
                        </Marker>
                      ))}
                    </MapContainer>
                  </div>
                  
                  {/* Detailed Data Table */}
                  <div className="movements-table-section">
                    <h2>تفاصيل التحركات</h2>
                    <div className="table-container">
                      <table className="movements-table">
                        <thead>
                          <tr>
                            <th>الوقت</th>
                            <th>السرعة (كم/س)</th>
                            <th>الحالة</th>
                            <th>الموقع (خط العرض/الطول)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trackingHistory.movementData.map((movement) => (
                            <tr 
                              key={movement.id}
                              className={isStopPoint(movement) ? 'stopped-row' : 'moving-row'}
                            >
                              <td>{movement.formattedTime}</td>
                              <td>{movement.speed.toFixed(1)}</td>
                              <td>
                                <span className={`status-badge ${isStopPoint(movement) ? 'stopped' : 'moving'}`}>
                                  {isStopPoint(movement) ? 'متوقف' : 'متحرك'}
                                </span>
                              </td>
                              <td>
                                {movement.location.lat.toFixed(6)}, {movement.location.lng.toFixed(6)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="driver-list-section">
              <div className="card">
                <div className="card-header">
                  <h2>بيانات السائقين</h2>
                  <div className="driver-stats">
                    <div className="stat">
                      <span className="stat-value">{drivers.length}</span>
                      <span className="stat-label">إجمالي السائقين</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">{drivers.filter(d => d.isRecentlyActive && d.isMoving).length}</span>
                      <span className="stat-label">يتحركون حالياً</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">{drivers.filter(d => d.isRecentlyActive && d.isAvailable).length}</span>
                      <span className="stat-label">متاح للطلبات</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">{drivers.filter(d => !d.isRecentlyActive).length}</span>
                      <span className="stat-label">غير نشطين</span>
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="driver-table">
                    <thead>
                      <tr>
                        <th>الاسم</th>
                        <th>الهاتف</th>
                        <th>الحالة</th>
                        <th>حالة الطلب</th>
                        <th>السرعة</th>
                        <th>آخر تحديث</th>
                        <th>الإجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drivers.map((driver) => (
                        <React.Fragment key={driver.id}>
                          <tr className={selectedDriver?.id === driver.id ? 'selected-row' : ''}>
                            <td>{driver.name}</td>
                            <td>
                              <div className="status-indicators">
                                <OnlineStatusPill driver={driver} />
                                <AvailabilityPill driver={driver} />
                              </div>
                            </td>
                            <td>
                              <div className={`status-badge ${getDriverStatusClass(driver)}`}>
                                {getDriverStatusText(driver)}
                              </div>
                            </td>
                            <td>
                              <span className={`order-status ${driver.orderStatus === 'لا يوجد طلب' ? 'no-order' : 'has-order'}`}>
                                {driver.orderStatus}
                              </span>
                            </td>
                            <td>
                              <div className="speed-indicator">
                                <div className="speed-value">{driver.speed.toFixed(1)}</div>
                                <div className="speed-unit">كم/س</div>
                              </div>
                            </td>
                            <td className={`last-update ${driver.timeSinceLastUpdate > 30 * 60 * 1000 ? 'last-update-old' : driver.timeSinceLastUpdate > 5 * 60 * 1000 ? 'last-update-medium' : 'last-update-recent'}`}>
                              {formatTimeSince(driver.timeSinceLastUpdate)}
                            </td>
                            <td>
                              <button
                                className="action-button"
                                onClick={() => setSelectedDriver(selectedDriver?.id === driver.id ? null : driver)}
                              >
                                {selectedDriver?.id === driver.id ? 'إلغاء' : 'إرسال رسالة'}
                              </button>
                            </td>
                          </tr>
                          {selectedDriver?.id === driver.id && (
                            <tr className="message-row">
                              <td colSpan="7">
                                <div className="message-container">
                                  <textarea
                                    value={messageBoxes[driver.id] || ''}
                                    onChange={(e) => handleMessageChange(driver.id, e.target.value)}
                                    placeholder="اكتب رسالتك هنا..."
                                    className="message-textarea"
                                  />
                                  <div className="message-actions">
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleSendMessage(driver.id);
                                      }}
                                      disabled={sending}
                                      className={`send-button ${sending ? 'sending' : ''}`}
                                    >
                                      {sending ? 'جاري الإرسال...' : 'إرسال'}
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverTracking;